/**
 * WEGO Favorites App - 検索結果ページお気に入り機能
 * data-product-variants属性からモーダルコンテンツを動的生成
 * Version: 2024-12-05-v3
 *
 * 一覧カードの表示:
 *   - data-variant-id がある場合はバリアント単位でお気に入り表示を判定
 *   - data-variant-id がない場合は商品単位で判定
 *   - 商品単位でマッチしない場合は data-product-variants 内のバリアントIDでフォールバック照合
 */

(function () {
  "use strict";
  console.log("[WEGO Search Favorites] Script loaded - Version 2024-12-05-v3");

  const API_BASE = "/apps/wego-favorite/api/favorites";
  const MAX_RETRIES = 3;
  const BUTTON_SELECTOR = ".vfa-favorite-btn--collection";
  const LOGIN_REQUIRED_CLASS = "vfa-favorite-btn--login-required";

  // お気に入り状態キャッシュ
  let favoriteProductIds = new Set();
  let favoriteVariantIds = new Set();
  let favoritesLoaded = false;
  let isInitializing = false;

  /**
   * 顧客IDを取得（数値ID）
   */
  function getCustomerId() {
    let customerId = null;

    const button = document.querySelector(
      `${BUTTON_SELECTOR}[data-customer-id]`,
    );
    if (button && button.dataset.customerId) {
      customerId = button.dataset.customerId;
    }

    if (!customerId && window.ShopifyCustomer && window.ShopifyCustomer.id) {
      customerId = String(window.ShopifyCustomer.id);
    }

    if (!customerId && window.__st && window.__st.cid) {
      customerId = String(window.__st.cid);
    }

    // 数値IDのみを返す
    if (customerId && /^\d+$/.test(customerId)) {
      return customerId;
    }

    // GID形式の場合は数値部分を抽出
    if (customerId && customerId.includes("gid://")) {
      return customerId.split("/").pop();
    }

    return customerId;
  }

  /**
   * variantIdを正規化（GID形式から数値IDを抽出、または数値IDをそのまま返す）
   * @param {string|number} variantId - variantId（GID形式または数値ID）
   * @returns {string} 正規化された数値ID
   */
  function normalizeVariantId(variantId) {
    if (!variantId) return "";
    const idStr = String(variantId);

    // 既に数値IDの場合はそのまま返す
    if (/^\d+$/.test(idStr)) {
      return idStr;
    }

    // GID形式の場合は数値部分を抽出
    if (idStr.includes("gid://")) {
      return idStr.replace(/^gid:\/\/shopify\/\w+\//, "");
    }

    // その他の形式の場合はそのまま返す
    return idStr;
  }

  /**
   * productIdを正規化（GID形式から数値IDを抽出、または数値IDをそのまま返す）
   * @param {string|number} productId - productId（GID形式または数値ID）
   * @returns {string} 正規化された数値ID
   */
  function normalizeProductId(productId) {
    if (!productId) return "";
    const idStr = String(productId);

    // 既に数値IDの場合はそのまま返す
    if (/^\d+$/.test(idStr)) {
      return idStr;
    }

    // GID形式の場合は数値部分を抽出
    if (idStr.includes("gid://")) {
      return idStr.replace(/^gid:\/\/shopify\/\w+\//, "");
    }

    // その他の形式の場合はそのまま返す
    return idStr;
  }

  /**
   * 初期化
   */
  function init() {
    if (isInitializing) return;
    isInitializing = true;

    console.log("[WEGO Search Favorites] 初期化開始");

    // モーダルは常に追加（ボタンの有無に関係なく）
    addFavoriteModal();
    addLoginModal();

    const buttons = document.querySelectorAll(BUTTON_SELECTOR);
    if (buttons.length === 0) {
      console.log(
        "[WEGO Search Favorites] ボタンが見つかりません。動的読み込みを待機",
      );
      isInitializing = false;
      return;
    }

    console.log(
      "[WEGO Search Favorites] 初期化:",
      buttons.length,
      "件のボタン",
    );

    const customerId = getCustomerId();
    if (customerId && !favoritesLoaded) {
      loadFavoriteStatuses();
    }

    setupEventListeners();
    isInitializing = false;
  }

  /**
   * お気に入り状態を一括取得
   */
  async function loadFavoriteStatuses() {
    const customerId = getCustomerId();
    if (!customerId) return;

    try {
      const params = new URLSearchParams({
        customerId: customerId,
        limit: "500",
      });

      const url = `${API_BASE}/list?${params}`;
      const response = await fetch(url);
      if (!response.ok) return;

      const result = await response.json();

      if (result.success && result.items) {
        favoriteProductIds.clear();
        favoriteVariantIds.clear();

        result.items.forEach((item) => {
          if (item.productId) {
            // productIdを正規化してから追加
            const normalizedProductId = normalizeProductId(item.productId);
            if (normalizedProductId) {
              favoriteProductIds.add(normalizedProductId);
            }
          }
          if (item.variantId) {
            // variantIdを正規化してから追加
            const normalizedVariantId = normalizeVariantId(item.variantId);
            if (normalizedVariantId) {
              favoriteVariantIds.add(normalizedVariantId);
            }
          }
        });

        favoritesLoaded = true;
        updateAllButtonStates();
      }
    } catch (error) {
      console.error("[WEGO Search Favorites] お気に入り状態取得エラー:", error);
    }
  }

  /**
   * ZETA描画カードにdata-variant-idを自動注入
   * color=all時、カードのリンクURL（?variant=XXXXX）からバリアントIDを取得してボタンに付与
   * これにより search-result-js.liquid の修正なしでバリアント単位判定が可能
   */
  function injectVariantIdsFromCardLinks() {
    const params = new URLSearchParams(window.location.search);
    if (params.get("color") !== "all") return;

    document
      .querySelectorAll(`${BUTTON_SELECTOR}[data-product-id]`)
      .forEach((button) => {
        if (button.dataset.variantId) return; // 既にある場合はスキップ
        const card = button.closest(".c-item");
        if (!card) return;
        const link = card.querySelector("a.c-item__thumbnail");
        if (!link) return;
        try {
          const url = new URL(link.href, location.origin);
          const variantFromUrl = url.searchParams.get("variant");
          if (variantFromUrl) {
            button.dataset.variantId = variantFromUrl;
          }
        } catch (e) {
          // URL解析エラーは無視
        }
      });
  }

  /**
   * 全ボタンの状態を更新
   * - data-variant-id がある場合はバリアント単位で判定
   * - data-variant-id がない場合は商品単位で判定
   * - 商品単位でマッチしない場合は data-product-variants 内のバリアントIDでフォールバック照合
   */
  function updateAllButtonStates() {
    // ZETA描画カードにdata-variant-idを自動注入（color=all時のみ）
    injectVariantIdsFromCardLinks();

    const buttons = document.querySelectorAll(
      `${BUTTON_SELECTOR}[data-product-id]`,
    );
    buttons.forEach((button) => {
      const variantId = button.dataset.variantId;
      const productId = button.dataset.productId;
      let isFavorited = false;

      if (variantId) {
        // カードが特定バリアントを表す場合: バリアント単位で判定
        const normalizedVariantId = normalizeVariantId(variantId);
        isFavorited = !!(
          normalizedVariantId && favoriteVariantIds.has(normalizedVariantId)
        );
      } else {
        // 従来の1商品1カード: 商品単位で判定
        const normalizedProductId = normalizeProductId(productId);
        isFavorited = !!(
          normalizedProductId && favoriteProductIds.has(normalizedProductId)
        );

        // 商品単位でマッチしない場合、data-product-variants内のバリアントIDでフォールバック照合
        // （color=all等でURL変更後にproductIdが変わるケースへの対応）
        if (!isFavorited) {
          try {
            const variantsJson = button.dataset.productVariants;
            if (variantsJson) {
              const variants = JSON.parse(variantsJson);
              isFavorited = variants.some((v) => {
                const nv = normalizeVariantId(String(v.id));
                return nv && favoriteVariantIds.has(nv);
              });
            }
          } catch (e) {
            // JSONパースエラーは無視
          }
        }
      }

      if (isFavorited) {
        button.classList.add("vfa-is-favorited");
        updateHeartIcon(button, true);
      } else {
        button.classList.remove("vfa-is-favorited");
        updateHeartIcon(button, false);
      }
    });

    // モーダル内のボタン
    document.querySelectorAll(".vfa-js-search-modal-fav-btn").forEach((btn) => {
      const variantId = btn.dataset.variantId;
      // variantIdを正規化してから比較
      const normalizedVariantId = normalizeVariantId(variantId);
      if (normalizedVariantId && favoriteVariantIds.has(normalizedVariantId)) {
        btn.classList.add("vfa-is-favorited");
        updateHeartIcon(btn, true);
      } else {
        btn.classList.remove("vfa-is-favorited");
        updateHeartIcon(btn, false);
      }
    });
  }

  /**
   * ハートアイコンの表示を更新
   */
  function updateHeartIcon(btn, isFavorited) {
    const defaultIcon = btn.querySelector(".vfa-favorite-icon--default");
    const activeIcon = btn.querySelector(".vfa-favorite-icon--active");
    if (defaultIcon && activeIcon) {
      defaultIcon.style.display = isFavorited ? "none" : "block";
      activeIcon.style.display = isFavorited ? "block" : "none";
    }
  }

  /**
   * イベントリスナー設定
   */
  function setupEventListeners() {
    document.querySelectorAll(BUTTON_SELECTOR).forEach((button) => {
      if (button.dataset.vfaSearchInitialized === "true") return;
      button.dataset.vfaSearchInitialized = "true";
      button.addEventListener("click", handleFavoriteClick);
    });
  }

  /**
   * お気に入りボタンクリック処理
   */
  function handleFavoriteClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const button = e.currentTarget;

    if (button.classList.contains(LOGIN_REQUIRED_CLASS)) {
      window.location.href =
        "/account/login?return_url=" +
        encodeURIComponent(window.location.pathname + window.location.search);
      return;
    }

    // ログイン済みユーザー: バリアント選択モーダルを表示
    const productId = button.dataset.productId;
    if (productId) {
      showFavoriteModal(button);
    } else {
      showNotification("商品情報を取得できませんでした", "error");
    }
  }

  /**
   * お気に入りモーダルを追加
   */
  function addFavoriteModal() {
    if (document.querySelector(".vfa-search-fav-modal")) return;

    const overlay = document.createElement("div");
    overlay.className = "vfa-search-fav-modal-overlay";
    overlay.addEventListener("click", closeFavoriteModal);
    document.body.appendChild(overlay);

    const modal = document.createElement("div");
    modal.className = "vfa-search-fav-modal";
    modal.innerHTML = `
      <div class="vfa-fav-modal-header">
        <h3>お気に入りに追加する</h3>
        <button class="vfa-fav-modal-close" aria-label="閉じる">✕</button>
      </div>
      <div class="vfa-fav-modal-body">
        <div class="vfa-fav-modal-loading">読み込み中...</div>
      </div>
    `;
    document.body.appendChild(modal);

    modal
      .querySelector(".vfa-fav-modal-close")
      .addEventListener("click", closeFavoriteModal);

    // モーダル用CSS追加
    addModalStyles();
  }

  /**
   * モーダル用CSSを追加
   */
  function addModalStyles() {
    if (document.querySelector("#vfa-search-fav-modal-styles")) return;

    const style = document.createElement("style");
    style.id = "vfa-search-fav-modal-styles";
    style.textContent = `
      .vfa-search-fav-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
        z-index: 9998;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      .vfa-search-fav-modal-overlay.vfa-is-open {
        opacity: 1;
        visibility: visible;
      }
      .vfa-search-fav-modal {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) scale(0.9);
        background: #fff;
        border-radius: 8px;
        width: 90%;
        max-width: 400px;
        max-height: 80vh;
        overflow: hidden;
        z-index: 9999;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease, transform 0.3s ease;
      }
      .vfa-search-fav-modal.vfa-is-open {
        opacity: 1;
        visibility: visible;
        transform: translate(-50%, -50%) scale(1);
      }
      .vfa-fav-modal-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 16px;
        border-bottom: 1px solid #eee;
      }
      .vfa-fav-modal-header h3 {
        margin: 0;
        font-size: 16px;
        font-weight: bold;
      }
      .vfa-fav-modal-close {
        background: none;
        border: none;
        font-size: 20px;
        cursor: pointer;
        padding: 4px 8px;
      }
      .vfa-fav-modal-body {
        padding: 16px;
        max-height: 60vh;
        overflow-y: auto;
      }
      .vfa-fav-modal-loading {
        text-align: center;
        padding: 20px;
        color: #666;
      }
      .vfa-fav-color-group {
        margin-bottom: 16px;
      }
      .vfa-fav-color-header {
        display: flex;
        align-items: center;
        gap: 10px;
        margin-bottom: 10px;
        padding-bottom: 8px;
        border-bottom: 1px solid #eee;
      }
      .vfa-fav-color-image {
        width: 50px;
        height: 60px;
        object-fit: cover;
        border-radius: 4px;
      }
      .vfa-fav-color-name {
        font-weight: bold;
        font-size: 14px;
      }
      .vfa-fav-size-list {
        list-style: none;
        padding: 0;
        margin: 0;
      }
      .vfa-fav-size-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 10px 0;
        border-bottom: 1px solid #f0f0f0;
      }
      .vfa-fav-size-item:last-child {
        border-bottom: none;
      }
      .vfa-fav-size-info {
        flex: 1;
      }
      .vfa-fav-size-name {
        font-size: 14px;
        margin-bottom: 4px;
      }
      .vfa-fav-size-name.vfa-is-unavailable {
        color: #999;
      }
      .vfa-fav-size-note {
        font-size: 12px;
        color: #666;
      }
      .vfa-fav-size-note--ship {
        color: #4caf50;
      }
      .vfa-fav-btn-modal {
        width: 28px;
        height: 28px;
        border: none;
        background: transparent;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        flex-shrink: 0;
        padding: 0;
        -webkit-tap-highlight-color: transparent;
      }
      .vfa-fav-btn-modal:hover {
        opacity: 0.7;
      }
      .vfa-fav-btn-modal .vfa-favorite-icon--default {
        display: block;
      }
      .vfa-fav-btn-modal .vfa-favorite-icon--active {
        display: none;
      }
      .vfa-fav-btn-modal.vfa-is-favorited .vfa-favorite-icon--default {
        display: none;
      }
      .vfa-fav-btn-modal.vfa-is-favorited .vfa-favorite-icon--active {
        display: block;
      }
      .vfa-fav-btn-modal.vfa-is-loading {
        opacity: 0.5;
        cursor: wait;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * お気に入りモーダルを表示（data-product-variantsから動的生成）
   */
  function showFavoriteModal(button) {
    const productId = button.dataset.productId;
    const productName = button.dataset.productName || "";
    const productImage = button.dataset.productImage || "";
    const productHandle = button.dataset.productHandle || "";
    let variants = [];

    try {
      const variantsJson = button.dataset.productVariants;
      if (variantsJson) {
        variants = JSON.parse(variantsJson);
        // Liquid {{ product.variants | json }} は価格を銭単位（×100）で出力する
        // ZETA JS描画の場合は円単位のため変換不要
        // Liquid固有フィールド（requires_shipping, taxable等）の有無で判定
        const isLiquidFormat =
          variants.length > 0 &&
          ("requires_shipping" in variants[0] || "taxable" in variants[0]);
        if (isLiquidFormat) {
          variants = variants.map(function (v) {
            return Object.assign({}, v, {
              price: typeof v.price === "number" ? v.price / 100 : v.price,
            });
          });
        }
      }
    } catch (e) {
      console.error("[WEGO Search Favorites] バリアント情報のパースエラー:", e);
    }

    const overlay = document.querySelector(".vfa-search-fav-modal-overlay");
    const modal = document.querySelector(".vfa-search-fav-modal");

    if (!modal) return;

    const body = modal.querySelector(".vfa-fav-modal-body");

    if (variants.length === 0) {
      body.innerHTML =
        '<div class="vfa-fav-modal-loading">バリアント情報を取得できませんでした</div>';
    } else {
      const colorGroups = groupVariantsByColor(variants);
      body.innerHTML = generateModalContent(
        colorGroups,
        productId,
        productHandle,
        productImage,
        productName,
      );
    }

    overlay.classList.add("vfa-is-open");
    modal.classList.add("vfa-is-open");

    updateModalButtonStates();
    setupModalButtonHandlers();
  }

  /**
   * バリアントをカラーごとにグループ化
   */
  function groupVariantsByColor(variants) {
    const groups = {};

    variants.forEach((variant) => {
      const color = variant.option1 || "デフォルト";
      const size = variant.option2 || variant.title;

      if (!groups[color]) {
        groups[color] = {
          color: color,
          image: variant.featured_image ? variant.featured_image.src : null,
          variants: [],
        };
      }

      groups[color].variants.push({
        id: variant.id,
        title: variant.title,
        size: size,
        available: variant.available,
        price: variant.price,
        option1: variant.option1,
        option2: variant.option2,
        option3: variant.option3,
      });
    });

    return Object.values(groups);
  }

  /**
   * モーダルコンテンツを生成
   */
  function generateModalContent(
    colorGroups,
    productId,
    productHandle,
    productImage,
    productName,
  ) {
    let html = "";

    colorGroups.forEach((group) => {
      html += `<div class="vfa-fav-color-group" data-color-name="${escapeHtml(
        group.color,
      )}">`;
      html += '<div class="vfa-fav-color-header">';

      if (group.image || productImage) {
        const imgSrc = group.image || productImage;
        html += `<img src="${escapeHtml(
          imgSrc,
        )}" class="vfa-fav-color-image" alt="${escapeHtml(
          group.color,
        )}" loading="lazy">`;
      }

      html += `<span class="vfa-fav-color-name">${escapeHtml(
        group.color,
      )}</span>`;
      html += "</div>";

      html += '<ul class="vfa-fav-size-list">';

      group.variants.forEach((variant) => {
        const sizeDisplay = variant.option2 || variant.title;
        const availabilityText = variant.available ? "在庫あり" : "在庫なし";
        const unavailableClass = variant.available ? "" : " vfa-is-unavailable";

        html += '<li class="vfa-fav-size-item">';
        html += '<div class="vfa-fav-size-info">';
        html += `<div class="vfa-fav-size-name${unavailableClass}">${escapeHtml(
          sizeDisplay,
        )} / ${availabilityText}</div>`;

        if (variant.available) {
          html +=
            '<div class="vfa-fav-size-note vfa-fav-size-note--ship">1~3営業日以内に発送予定</div>';
        }

        html += "</div>";

        // variantIdを正規化
        const normalizedVariantId = normalizeVariantId(variant.id);

        // 商品カードのボタンからアイコンURLを取得（存在する場合）
        const cardButton = document.querySelector(
          `${BUTTON_SELECTOR}[data-product-id="${productId}"]`,
        );
        let defaultIconSrc = "";
        let activeIconSrc = "";

        // 方法A: theme.liquidで定義されたグローバル変数から取得（推奨）
        if (window.VFA_ASSETS) {
          defaultIconSrc = window.VFA_ASSETS.heartGy || "";
          activeIconSrc = window.VFA_ASSETS.heartSolid || "";
        }

        // 方法B: 商品カードのボタンから取得
        if (cardButton) {
          const defaultIcon = cardButton.querySelector(
            ".vfa-favorite-icon--default",
          );
          const activeIcon = cardButton.querySelector(
            ".vfa-favorite-icon--active",
          );
          if (defaultIcon && defaultIcon.src) {
            defaultIconSrc = defaultIcon.src;
          }
          if (activeIcon && activeIcon.src) {
            activeIconSrc = activeIcon.src;
          }
        }

        // フォールバック: ページ内の最初のボタンから取得
        if (!defaultIconSrc || !activeIconSrc) {
          const firstButton = document.querySelector(BUTTON_SELECTOR);
          if (firstButton) {
            const defaultIcon = firstButton.querySelector(
              ".vfa-favorite-icon--default",
            );
            const activeIcon = firstButton.querySelector(
              ".vfa-favorite-icon--active",
            );
            if (defaultIcon && defaultIcon.src && !defaultIconSrc) {
              defaultIconSrc = defaultIcon.src;
            }
            if (activeIcon && activeIcon.src && !activeIconSrc) {
              activeIconSrc = activeIcon.src;
            }
          }
        }

        // それでも見つからない場合は警告を出力
        if (!defaultIconSrc || !activeIconSrc) {
          console.warn(
            "[WEGO Search Favorites] アイコンURLが見つかりません。theme.liquidにVFA_ASSETSの定義を追加してください。",
          );
        }

        html += `
          <button
            class="vfa-fav-btn-modal vfa-js-search-modal-fav-btn"
            data-product-id="${productId}"
            data-variant-id="${normalizedVariantId}"
            data-handle="${escapeHtml(productHandle)}"
            data-price="${variant.price || 0}"
            data-product-name="${escapeHtml(productName || "")}"
            aria-label="お気に入り">
            <img class="vfa-favorite-icon vfa-favorite-icon--default" src="${escapeHtml(defaultIconSrc)}" alt="お気に入り" width="22" height="22">
            <img class="vfa-favorite-icon vfa-favorite-icon--active" src="${escapeHtml(activeIconSrc)}" alt="お気に入り済み" width="22" height="22" style="display:none;">
          </button>
        `;

        html += "</li>";
      });

      html += "</ul>";
      html += "</div>";
    });

    return html;
  }

  /**
   * HTMLエスケープ
   */
  function escapeHtml(str) {
    if (!str) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /**
   * モーダル内のボタン状態を更新
   */
  function updateModalButtonStates() {
    document
      .querySelectorAll(".vfa-fav-modal-body .vfa-js-search-modal-fav-btn")
      .forEach((btn) => {
        const variantId = btn.dataset.variantId;
        // variantIdを正規化してから比較
        const normalizedVariantId = normalizeVariantId(variantId);
        if (
          normalizedVariantId &&
          favoriteVariantIds.has(normalizedVariantId)
        ) {
          btn.classList.add("vfa-is-favorited");
          updateHeartIcon(btn, true);
        } else {
          btn.classList.remove("vfa-is-favorited");
          updateHeartIcon(btn, false);
        }
      });
  }

  /**
   * モーダル内ボタンのイベントハンドラー設定
   */
  function setupModalButtonHandlers() {
    document
      .querySelectorAll(".vfa-fav-modal-body .vfa-js-search-modal-fav-btn")
      .forEach((btn) => {
        if (btn.dataset.vfaModalInitialized === "true") return;
        btn.dataset.vfaModalInitialized = "true";
        btn.addEventListener("click", handleModalFavoriteClick);
      });
  }

  /**
   * モーダル内お気に入りボタンクリック処理
   */
  async function handleModalFavoriteClick(e) {
    e.preventDefault();
    e.stopPropagation();

    const btn = e.currentTarget;
    if (btn.classList.contains("vfa-is-loading") || btn.disabled) return;

    const productId = btn.dataset.productId;
    const variantId = btn.dataset.variantId;

    // variantIdを正規化
    const normalizedVariantId = normalizeVariantId(variantId);

    const customerId = getCustomerId();
    if (!customerId) {
      closeFavoriteModal();
      window.location.href =
        "/account/login?return_url=" +
        encodeURIComponent(window.location.pathname + window.location.search);
      return;
    }

    const isFavorited = btn.classList.contains("vfa-is-favorited");

    // お気に入り済みの場合は削除不可（マイページからのみ削除可能）
    if (isFavorited) {
      alert("すでにお気に入り登録されています");
      return;
    }

    btn.classList.add("vfa-is-loading");

    // 楽観的UI更新
    btn.classList.toggle("vfa-is-favorited");
    updateHeartIcon(btn, !isFavorited);

    if (isFavorited) {
      favoriteVariantIds.delete(normalizedVariantId);
    } else {
      favoriteVariantIds.add(normalizedVariantId);
    }

    // API呼び出し
    let success = false;
    let retryCount = 0;

    while (retryCount <= MAX_RETRIES && !success) {
      try {
        let response;
        let endpoint;
        let body;

        if (isFavorited) {
          endpoint = `${API_BASE}/remove`;
          body = {
            customerId: customerId,
            variantId: variantId, // APIには元のvariantIdを送信（GID形式または数値ID、API側で処理）
          };
        } else {
          // モーダルボタンのdata属性から価格と商品名を取得
          // 価格はshowFavoriteModal()でデータソース判定済み（Liquid銭単位→円変換済み、ZETA→そのまま）
          const variantPrice = parseFloat(btn.dataset.price) || 0;
          const pName = btn.dataset.productName || "";
          endpoint = `${API_BASE}/add`;
          body = {
            customerId: customerId,
            productId: productId,
            variantId: variantId, // APIには元のvariantIdを送信（GID形式または数値ID、API側で処理）
            productName: pName,
            price: variantPrice,
            imageUrl: "",
          };
        }

        response = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          success = true;
          updateProductCardState(productId);
          showNotification(
            isFavorited
              ? "お気に入りから削除しました"
              : "お気に入りに追加しました",
            "success",
          );
        } else if (response.status >= 500 && retryCount < MAX_RETRIES) {
          retryCount++;
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * retryCount),
          );
          continue;
        } else {
          throw new Error(data.error || data.message || "API Error");
        }
      } catch (error) {
        if (retryCount < MAX_RETRIES) {
          retryCount++;
          await new Promise((resolve) =>
            setTimeout(resolve, 1000 * retryCount),
          );
          continue;
        }

        // ロールバック
        btn.classList.toggle("vfa-is-favorited");
        updateHeartIcon(btn, isFavorited);
        if (isFavorited) {
          favoriteVariantIds.add(normalizedVariantId);
        } else {
          favoriteVariantIds.delete(normalizedVariantId);
        }

        showNotification("エラーが発生しました", "error");
      }
    }

    btn.classList.remove("vfa-is-loading");
  }

  /**
   * 商品カードの状態を更新
   * - data-variant-id があるカードはバリアント単位で更新
   * - data-variant-id がないカードは商品単位で更新
   */
  function updateProductCardState(productId) {
    const modalButtons = document.querySelectorAll(
      `.vfa-fav-modal-body .vfa-js-search-modal-fav-btn[data-product-id="${productId}"]`,
    );
    let hasAnyFavorite = false;
    modalButtons.forEach((btn) => {
      const normalizedVariantId = normalizeVariantId(btn.dataset.variantId);
      if (normalizedVariantId && favoriteVariantIds.has(normalizedVariantId)) {
        hasAnyFavorite = true;
      }
    });

    const normalizedProductId = normalizeProductId(productId);

    // 同一productIdのカードを全て取得して更新（複数カード対応）
    const cardButtons = document.querySelectorAll(
      `${BUTTON_SELECTOR}[data-product-id="${productId}"]`,
    );
    cardButtons.forEach((cardButton) => {
      const variantId = cardButton.dataset.variantId;
      let isFavorited;

      if (variantId) {
        // カードが特定バリアントを表す場合: バリアント単位で判定
        const normalizedVariantId = normalizeVariantId(variantId);
        isFavorited = !!(
          normalizedVariantId && favoriteVariantIds.has(normalizedVariantId)
        );
      } else {
        // 従来の1商品1カード: いずれかのバリアントがお気に入りなら表示
        isFavorited = hasAnyFavorite;
        if (normalizedProductId) {
          if (isFavorited) {
            favoriteProductIds.add(normalizedProductId);
          } else {
            favoriteProductIds.delete(normalizedProductId);
          }
        }
      }

      if (isFavorited) {
        cardButton.classList.add("vfa-is-favorited");
        updateHeartIcon(cardButton, true);
      } else {
        cardButton.classList.remove("vfa-is-favorited");
        updateHeartIcon(cardButton, false);
      }
    });
  }

  /**
   * お気に入りモーダルを閉じる
   */
  function closeFavoriteModal() {
    const overlay = document.querySelector(".vfa-search-fav-modal-overlay");
    const modal = document.querySelector(".vfa-search-fav-modal");
    if (overlay) overlay.classList.remove("vfa-is-open");
    if (modal) modal.classList.remove("vfa-is-open");
  }

  /**
   * ログインモーダルを追加
   */
  function addLoginModal() {
    if (document.querySelector(".vfa-search-login-modal")) return;

    const modal = document.createElement("div");
    modal.className = "vfa-search-login-modal";
    modal.innerHTML = `
      <div class="vfa-login-modal-overlay"></div>
      <div class="vfa-login-modal-wrapper">
        <div class="vfa-login-modal-header">
          <button class="vfa-login-modal-close vfa-js-search-login-close">✕</button>
        </div>
        <div class="vfa-login-modal-content">
          <h3>お気に入り登録するには<br>会員登録・ログインが必要です</h3>
          <button class="c-btn c-btn--primary vfa-js-search-login-btn">新規会員登録・ログイン</button>
          <button class="c-btn c-btn--border vfa-js-search-login-close">キャンセル</button>
        </div>
      </div>
    `;

    // ログインモーダル用スタイル追加
    const style = document.createElement("style");
    style.id = "vfa-search-login-modal-styles";
    style.textContent = `
      .vfa-search-login-modal {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10000;
        opacity: 0;
        visibility: hidden;
        transition: opacity 0.3s ease, visibility 0.3s ease;
      }
      .vfa-search-login-modal.vfa-is-visible {
        opacity: 1;
        visibility: visible;
      }
      .vfa-search-login-modal .vfa-login-modal-overlay {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.5);
      }
      .vfa-search-login-modal .vfa-login-modal-wrapper {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: #fff;
        border-radius: 8px;
        width: 90%;
        max-width: 350px;
        padding: 20px;
      }
      .vfa-search-login-modal .vfa-login-modal-header {
        text-align: right;
        margin-bottom: 10px;
      }
      .vfa-search-login-modal .vfa-login-modal-close {
        background: none;
        border: none;
        font-size: 20px;
        cursor: pointer;
      }
      .vfa-search-login-modal .vfa-login-modal-content {
        text-align: center;
      }
      .vfa-search-login-modal .vfa-login-modal-content h3 {
        font-size: 16px;
        margin-bottom: 20px;
        line-height: 1.5;
      }
      .vfa-search-login-modal .vfa-login-modal-content .c-btn {
        display: block;
        width: 100%;
        padding: 12px;
        margin-bottom: 10px;
        border-radius: 4px;
        font-size: 14px;
        cursor: pointer;
      }
      .vfa-search-login-modal .vfa-login-modal-content .c-btn--primary {
        background: #000;
        color: #fff;
        border: none;
      }
      .vfa-search-login-modal .vfa-login-modal-content .c-btn--border {
        background: #fff;
        color: #000;
        border: 1px solid #000;
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(modal);

    modal
      .querySelector(".vfa-js-search-login-btn")
      .addEventListener("click", () => {
        const returnUrl = encodeURIComponent(
          window.location.pathname + window.location.search,
        );
        window.location.href = `/account/login?return_url=${returnUrl}`;
      });

    modal.querySelectorAll(".vfa-js-search-login-close").forEach((btn) => {
      btn.addEventListener("click", hideLoginModal);
    });

    modal
      .querySelector(".vfa-login-modal-overlay")
      .addEventListener("click", hideLoginModal);
  }

  /**
   * ログインモーダル表示
   */
  function showLoginModal() {
    const modal = document.querySelector(".vfa-search-login-modal");
    if (modal) modal.classList.add("vfa-is-visible");
  }

  /**
   * ログインモーダル非表示
   */
  function hideLoginModal() {
    const modal = document.querySelector(".vfa-search-login-modal");
    if (modal) modal.classList.remove("vfa-is-visible");
  }

  /**
   * 通知表示
   */
  function showNotification(message, type = "success") {
    const existing = document.querySelector(
      ".vfa-search-favorite-notification",
    );
    if (existing) existing.remove();

    const notification = document.createElement("div");
    notification.className = `vfa-search-favorite-notification vfa-search-favorite-notification--${type}`;
    notification.textContent = message;

    if (!document.querySelector("#vfa-search-notification-styles")) {
      const style = document.createElement("style");
      style.id = "vfa-search-notification-styles";
      style.textContent = `
        .vfa-search-favorite-notification {
          position: fixed;
          top: 20px;
          left: 50%;
          transform: translateX(-50%);
          padding: 12px 24px;
          border-radius: 4px;
          font-size: 14px;
          z-index: 99999;
          animation: vfa-search-toast-in 0.3s ease;
        }
        .vfa-search-favorite-notification--success {
          background: #4caf50;
          color: #fff;
        }
        .vfa-search-favorite-notification--error {
          background: #f44336;
          color: #fff;
        }
        .vfa-search-favorite-notification--info {
          background: #333;
          color: #fff;
        }
        @keyframes vfa-search-toast-in {
          from {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
        }
        @keyframes vfa-search-toast-out {
          from {
            opacity: 1;
            transform: translateX(-50%) translateY(0);
          }
          to {
            opacity: 0;
            transform: translateX(-50%) translateY(-20px);
          }
        }
      `;
      document.head.appendChild(style);
    }

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = "vfa-search-toast-out 0.3s ease";
      setTimeout(() => notification.remove(), 300);
    }, 2000);
  }

  /**
   * 新しく追加されたボタンを初期化
   */
  function initializeNewButtons() {
    const uninitializedButtons = document.querySelectorAll(
      `${BUTTON_SELECTOR}:not([data-vfa-search-initialized="true"])`,
    );
    if (uninitializedButtons.length === 0) return;

    addFavoriteModal();
    addLoginModal();

    setupEventListeners();

    if (favoritesLoaded) {
      updateAllButtonStates();
    } else {
      const customerId = getCustomerId();
      if (customerId) {
        loadFavoriteStatuses();
      }
    }
  }

  // 初期化
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // 動的コンテンツ対応（MutationObserver）
  let debounceTimer;
  const observer = new MutationObserver((mutations) => {
    let hasNewButtons = false;
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          if (node.matches && node.matches(BUTTON_SELECTOR)) {
            hasNewButtons = true;
          } else if (
            node.querySelector &&
            node.querySelector(BUTTON_SELECTOR)
          ) {
            hasNewButtons = true;
          }
        }
      });
    });

    if (hasNewButtons) {
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        initializeNewButtons();
      }, 300);
    }
  });

  const observeTarget =
    document.getElementById("CollectionProductGrid") || document.body;
  observer.observe(observeTarget, {
    childList: true,
    subtree: true,
  });

  // 定期的にボタンをチェック（MutationObserverのフォールバック）
  let retryCount = 0;
  const maxRetries = 20;
  const checkInterval = setInterval(() => {
    retryCount++;
    const buttons = document.querySelectorAll(
      `${BUTTON_SELECTOR}:not([data-vfa-search-initialized="true"])`,
    );
    if (buttons.length > 0) {
      initializeNewButtons();
      clearInterval(checkInterval);
    } else if (retryCount >= maxRetries) {
      clearInterval(checkInterval);
    }
  }, 500);
})();
